import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiDownload, FiX } from 'react-icons/fi';
import { showToast } from '../../utils/toast';
import { formatSignedUsd } from '../../utils/formatUsd';
import tradePilotLogo from '../../assets/copy-image.png';

export type PnlShareScope = 'day' | 'month' | 'year';

export type PnlSharePayload = {
  scope: PnlShareScope;
  title: string;
  subtitle: string;
  pnl: number;
  tradesCount: number;
  winLossRatio?: string;
  winRate?: number;
};

type SharePnlReportModalProps = {
  payload: PnlSharePayload;
  onClose: () => void;
};

const scopeBadge = (scope: PnlShareScope) => {
  if (scope === 'day') return { label: 'DAY', bg: 'rgba(59,130,246,0.2)', color: '#93c5fd' };
  if (scope === 'month') return { label: 'MONTH', bg: 'rgba(168,85,247,0.2)', color: '#d8b4fe' };
  return { label: 'YEAR', bg: 'rgba(245,158,11,0.2)', color: '#fcd34d' };
};

const SharePnlReportModal: React.FC<SharePnlReportModalProps> = ({ payload, onClose }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const badge = scopeBadge(payload.scope);
  const isProfit = payload.pnl >= 0;

  useEffect(() => {
    let active = true;
    const convertImage = async () => {
      try {
        const response = await fetch(tradePilotLogo);
        if (!response.ok) throw new Error('Image not found');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          if (active) setBase64Image(reader.result as string);
        };
        reader.readAsDataURL(blob);
      } catch {
        console.warn('Decorative image could not be loaded, card will generate without it.');
      }
    };
    convertImage();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const generateCanvas = async (
    element: HTMLElement,
    includeImage: boolean
  ): Promise<HTMLCanvasElement> => {
    // @ts-ignore html2canvas from CDN
    return await window.html2canvas(element, {
      backgroundColor: '#0f172a',
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      width: 500,
      onclone: (doc: Document) => {
        const el = doc.querySelector('.share-pnl-card-container') as HTMLElement | null;
        if (el) {
          el.style.transform = 'none';
          el.style.boxShadow = 'none';
          el.style.margin = '0';
        }
        if (!includeImage) {
          const img = doc.querySelector('.decorative-image');
          if (img) img.remove();
        }
      },
    });
  };

  const handleDownload = async () => {
    if (!cardRef.current || isGenerating) return;
    setIsGenerating(true);

    try {
      // @ts-ignore
      if (typeof window.html2canvas === 'undefined') {
        throw new Error('Generator loading...');
      }

      await new Promise((r) => setTimeout(r, 200));

      let canvas: HTMLCanvasElement;
      try {
        canvas = await generateCanvas(cardRef.current, true);
      } catch (firstError) {
        console.warn('Standard generation failed, retrying in safe mode...', firstError);
        canvas = await generateCanvas(cardRef.current, false);
      }

      await new Promise<void>((resolve, reject) => {
        canvas.toBlob((blob: Blob | null) => {
          if (!blob) {
            reject(new Error('Failed to create image'));
            return;
          }
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          const stamp = new Date().toISOString().slice(0, 10);
          link.download = `trade-pilot-${payload.scope}-pnl-${stamp}.png`;
          link.href = url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          resolve();
        }, 'image/png');
      });

      showToast('success', 'P&L report downloaded');
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to generate share image');
    } finally {
      setIsGenerating(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-3 sm:p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] sm:max-h-[99vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-surface shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-border p-4 sm:p-6">
          <h3 className="min-w-0 text-xl sm:text-2xl font-bold text-foreground">
            Share {payload.scope === 'day' ? 'Day' : payload.scope === 'month' ? 'Month' : 'Year'} P&amp;L
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 transition-colors hover:bg-surface-elevated"
            aria-label="Close"
          >
            <FiX className="h-6 w-6 text-muted" />
          </button>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-4 border-b border-border p-4 sm:p-6">
          <button type="button" onClick={onClose} className="btn-secondary w-full sm:w-auto" disabled={isGenerating}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={isGenerating}
            className={`btn-primary flex items-center justify-center gap-2 w-full sm:w-auto ${isGenerating ? 'opacity-50' : ''}`}
          >
            {isGenerating ? (
              <span className="animate-pulse">Generating...</span>
            ) : (
              <>
                <FiDownload className="h-5 w-5" />
                <span>Download Image</span>
              </>
            )}
          </button>
        </div>

        <div className="bg-black/20 p-3 sm:p-6">
          <div className="overflow-x-auto">
          <div className="mx-auto w-max">
          <div
            ref={cardRef}
            className="share-pnl-card-container"
            style={{
              width: 500,
              minWidth: 500,
              borderRadius: 20,
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
              fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
              color: '#e2e8f0',
            }}
          >
            <div style={{ padding: '28px 28px 0', position: 'relative' }}>
              <div
                style={{
                  position: 'absolute',
                  inset: '0 0 auto 0',
                  height: 120,
                  background:
                    'radial-gradient(ellipse 90% 100% at 50% 0%, rgba(56,189,248,0.12), transparent 70%)',
                  pointerEvents: 'none',
                }}
              />

              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 18,
                }}
              >
                <div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
                    Trade Pilot
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#94a3b8',
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Trading Report
                  </div>
                </div>
                <div
                  style={{
                    padding: '7px 14px',
                    borderRadius: 10,
                    background: badge.bg,
                    color: badge.color,
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                  }}
                >
                  {badge.label}
                </div>
              </div>

              <div
                style={{
                  position: 'relative',
                  fontSize: 28,
                  fontWeight: 800,
                  color: '#fff',
                  letterSpacing: '-0.03em',
                  lineHeight: 1.15,
                }}
              >
                {payload.title}
              </div>
              <div style={{ position: 'relative', marginTop: 6, fontSize: 13, color: '#94a3b8' }}>
                {payload.subtitle}
              </div>

              <div
                style={{
                  position: 'relative',
                  marginTop: 22,
                  marginBottom: 8,
                  padding: '22px 20px',
                  borderRadius: 16,
                  background:
                    'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(15,23,42,0.95) 100%)',
                  border: '1px solid rgba(71,85,105,0.4)',
                }}
              >
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#94a3b8',
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    marginBottom: 10,
                  }}
                >
                  Total Profit
                </div>
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: 42,
                    fontWeight: 800,
                    letterSpacing: '-0.04em',
                    color: isProfit ? '#22c55e' : '#ef4444',
                    lineHeight: 1.1,
                    marginBottom: 14,
                  }}
                >
                  {formatSignedUsd(payload.pnl)}
                </div>
                <div style={{ height: 1, background: 'rgba(71,85,105,0.55)', marginBottom: 12 }} />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: '#94a3b8',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    Total Trades
                  </span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>
                    {payload.tradesCount.toLocaleString()}
                  </span>
                </div>
                {(payload.winLossRatio || payload.winRate != null) && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 12,
                      marginTop: 10,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        color: '#94a3b8',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}
                    >
                      Win Rate
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#93c5fd' }}>
                      {payload.winRate != null ? `${payload.winRate.toFixed(1)}%` : ''}
                      {payload.winLossRatio ? ` · ${payload.winLossRatio}` : ''}
                    </span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 10px' }}>
                {base64Image && (
                  <img
                    src={base64Image}
                    alt="Trade Pilot"
                    className="decorative-image"
                    style={{
                      width: 320,
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block',
                      borderRadius: 12,
                      filter: 'drop-shadow(0 12px 28px rgba(0,0,0,0.4))',
                    }}
                  />
                )}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 16,
                padding: '14px 28px 20px',
                borderTop: '1px solid rgba(71,85,105,0.35)',
                background: 'rgba(30,41,59,0.35)',
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>Trade Pilot</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>AI Copy Trading</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>thetradepilot.ai</div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>
                  {new Date().toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
              </div>
            </div>
          </div>
          </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SharePnlReportModal;
